import { type FC } from "react";
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import CustomChartTooltip from "src/components/reusable/CustomChartTooltip/CustomChartTooltip";
import {
  CHART_AXIS_STROKE,
  CHART_COLOR_PALETTE,
  CHART_GRID_STROKE,
  CHART_TICK_COLOR,
  CHART_TOOLTIP_CURSOR,
} from "src/components/charts/chartPalette";
import { mirrorChartMargin, mirrorHorizontalBarRadius, rtlChartProps } from "src/components/charts/chartRtl";
import RestaurantSectionCard from "../RestaurantSectionCard";
import { useRestaurantFormatters } from "../../utils/restaurantFormatters";

export interface HorizontalBarDatum {
  id: string;
  label: string;
  value: number;
}

interface RestaurantHorizontalBarsProps {
  title: string;
  tip?: string;
  data: HorizontalBarDatum[];
  valueKind?: "currency" | "number";
}

const RestaurantHorizontalBars: FC<RestaurantHorizontalBarsProps> = ({
  title,
  tip,
  data,
  valueKind = "currency",
}) => {
  const fmt = useRestaurantFormatters();
  const formatValue = (value: number) =>
    valueKind === "currency" ? fmt.formatCurrency(value, { compact: true }) : fmt.formatNumber(value);
  const minHeight = Math.max(220, data.length * 34);
  const chartRtl = rtlChartProps(fmt.isRTL);
  const longestLabel = data.reduce((max, item) => Math.max(max, item.label.length), 0);
  const yAxisWidth = Math.min(160, Math.max(70, longestLabel * 7 + 16));

  return (
    <RestaurantSectionCard title={title} tip={tip}>
      <div className="restaurant-chart restaurant-chart--horizontal-bars" style={{ minHeight }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={data}
            layout="vertical"
            margin={mirrorChartMargin({ top: 4, right: 16, left: 0, bottom: 0 }, fmt.isRTL)}
          >
            <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID_STROKE} horizontal={false} />
            <XAxis
              type="number"
              reversed={chartRtl.axisReversed}
              tick={{ fontSize: 11, fill: CHART_TICK_COLOR }}
              axisLine={{ stroke: CHART_AXIS_STROKE }}
              tickLine={false}
              tickFormatter={value => formatValue(Number(value))}
            />
            <YAxis
              type="category"
              dataKey="label"
              orientation={chartRtl.yAxisOrientation}
              width={yAxisWidth}
              tick={{ fontSize: 12, fill: CHART_TICK_COLOR }}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip
              content={<CustomChartTooltip formatTooltip={value => formatValue(value)} hideSeriesName />}
              cursor={{ fill: CHART_TOOLTIP_CURSOR }}
              wrapperStyle={{ pointerEvents: "none" }}
            />
            <Bar dataKey="value" radius={mirrorHorizontalBarRadius(4, fmt.isRTL)} maxBarSize={18}>
              {data.map((item, index) => (
                <Cell key={item.id} fill={CHART_COLOR_PALETTE[index % CHART_COLOR_PALETTE.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </RestaurantSectionCard>
  );
};

export default RestaurantHorizontalBars;
